<script setup>
import { ref, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
/* global axios */
// 与后端 app/controllers/api/v1/accounts/contacts_controller.rb 的
// RESULTS_PER_PAGE 对齐,用于计算跨页连续序号。
const CONTACTS_PER_PAGE = 15;

const agents = useMapGetter('agents/getAgents');
// 归属者: 优先 custom_attributes.owner(留电时adapter写入);
// 没留电没owner的, 从contact所在收件箱的创建者兜底(动态, 新inbox免配置)。
const resolveOwner = contact => {
  if (contact.customAttributes?.owner) return contact.customAttributes.owner;
  const ci = contact.contactInboxes?.[0] || contact.contact_inboxes?.[0];
  const creator =
    ci?.inbox?.createdById || ci?.inbox?.created_by_id;
  const byCreator =
    creator && agents.value
      ? agents.value.find(a => a.id === creator)?.name
      : '';
  return byCreator || '-';
};

// 来源URL: 嵌入式显示落地页(additional_attributes.referer);
// 直链场景 referer 为空, 兜底显示 custom_attributes.referrerUrl(完整来源页 URL, 由直链抓取写入)。
// 注意: dashboard 会把 custom_attributes 的 key 驼峰化(referrer_url→referrerUrl),
// 同 contactSavedAt/cardStatus; 写入时后端存蛇形 referrer_url。
const sourceUrl = contact =>
  contact.additionalAttributes?.referer ||
  contact.customAttributes?.referrerUrl ||
  '';

const props = defineProps({
  contacts: { type: Array, required: true },
  currentPage: { type: Number, default: 1 },
  // 定制: 每页条数(与后端 per_page 对齐, 用于跨页连续序号)
  perPage: { type: Number, default: 15 },
  // 仅消息记录(active)视图开启: 已留联系方式(电话或邮箱)的行高亮浅绿
  highlightContactInfo: { type: Boolean, default: false },
  // 保留声明,避免父组件传入时 fallthrough 到根元素成为无效 DOM 属性
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
});

// view-details: 点击名片不再跳详情页,改为右侧抽屉展开(由父组件 ContactsIndex 处理)
const emit = defineEmits(['toggleContact', 'view-details']);

// 定制(2026-08-05): 重复联系方式标记。duplicateMap: {contact_id: {is_first: bool}}
const duplicateMap = ref({});
onMounted(async () => {
  try {
    const m = window.location.pathname.match(/accounts\/(\d+)/);
    if (m) {
      const { data } = await axios.get(`/api/v1/accounts/${m[1]}/contacts/duplicate_map`);
      duplicateMap.value = data;
    }
  } catch {
    // 静默失败,不影响列表
  }
});

const rowNumberOf = index =>
  (props.currentPage - 1) * (props.perPage || CONTACTS_PER_PAGE) + index + 1;

// 已留联系方式: 电话/邮箱/微信/QQ/Telegram 任一(消息记录视图里这些客户更值得跟进,行高亮浅绿)
const hasContactInfo = contact => {
  const ca = contact.customAttributes || {};
  return !!(
    contact.phoneNumber ||
    contact.phone_number ||
    ca.phone ||
    contact.email ||
    ca.wechat ||
    ca.qq ||
    ca.telegram
  );
};

// 联系方式列显示: 电话优先; 无电话时回退显示 微信/QQ/固话/Telegram(带标签)。
// 纯 QQ/微信留联客户(如 wild-lake-405)之前电话邮箱两列都是"-", 看起来像没识别。
const contactMethodDisplay = contact => {
  const phone = (contact.phoneNumber || contact.customAttributes?.phone || '')
    .replace(/^\+86[-\s]?/, '');
  if (phone) return phone;
  const ca = contact.customAttributes || {};
  if (ca.wechat) return `微信 ${ca.wechat}`;
  if (ca.qq) return `QQ ${ca.qq}`;
  if (ca.landline) return `固话 ${ca.landline}`;
  if (ca.telegram) return `TG ${ca.telegram}`;
  return '-';
};

// 首次进入时间: contact.createdAt 是后端 to_i 的 unix 秒, 格式化成可读串
// 显式按北京时间(Asia/Shanghai)格式化 Date, 不依赖浏览器时区(服务器/容器可能是 UTC)
const formatInBeijing = d => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Shanghai',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const g = type => parts.find(p => p.type === type)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}:${g('minute')}`;
};

const formatEntryTime = ts => {
  if (!ts) return '-';
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return '-';
  return formatInBeijing(d);
};

// 保存时间显示: contact_saved_at 是 UTC 字符串(YYYY-MM-DD HH:MM), 按 UTC 解析后转北京时间显示
const formatSavedTime = t => {
  if (!t) return '';
  const d = new Date(t.replace(' ', 'T') + ':00Z');
  if (Number.isNaN(d.getTime())) return t;
  return formatInBeijing(d);
};

// 名片状态对应颜色：未识别已同步=绿，已查看无留联=灰
const cardStatusColorClass = status => {
  if (status === '未识别已同步') return 'bg-green-500';
  if (status === '已查看无留联') return 'bg-slate-400';
  return 'bg-transparent border border-solid border-n-slate-4';
};

// 来源 ASCII key → 中文标签映射（SDK 改发 ASCII key 防乱码；旧值/未知原样显示）
const SOURCE_LABELS = {
  direct: '直接访问',
  baidu: '百度',
  bing: '必应',
  google: '谷歌',
  '360': '360搜索',
  sogou: '搜狗',
  zhihu: '知乎',
  douyin: '抖音',
  weibo: '微博',
  tencent: '腾讯',
  other: '其他',
};
const sourceLabel = src => {
  if (!src) return '-';
  if (SOURCE_LABELS[src]) return SOURCE_LABELS[src];
  if (src.startsWith('ad:')) return `广告:${src.slice(3)}`;
  return src;
};

// 定制: 记住最近查看的名片行——点击后整行加深蓝底色, 关闭右侧面板后仍保留,
// 方便客服确认"上次改的是哪条"。再点别的名片才换高亮。
const lastViewedId = ref(null);
const rowBgClass = contact => {
  if (contact.id === lastViewedId.value) return 'bg-[#3b82f6]/25';
  if (props.highlightContactInfo && hasContactInfo(contact))
    return 'bg-green-500/15';
  return 'hover:bg-n-brand/5';
};
const onClickViewDetails = id => {
  lastViewedId.value = id;
  emit('view-details', id);
};

// 定制(2026-08-14): 访客名称 = 地域｜归属客服(仿易聊/快商通), 与尘锋推送 visitor_display_name 同规则。
// 完整→地域｜客服(北京｜杨洋); 无客服→纯地域(浙江杭州); 都无→退化为访客id。非唯一, 仅显示用。
const visitorName = contact => {
  const attrs = contact.additionalAttributes || {};
  const custom = contact.customAttributes || {};
  const region = [attrs.province, attrs.city]
    .map(v => (v || '').trim())
    .filter(Boolean)
    .filter(v => v !== 'XX')
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join('');
  const owner = (custom.owner || '').trim();
  if (region && owner) return `${region}｜${owner}`;
  if (region) return region;
  return owner || (contact.name || '').trim() || '-';
};
</script>

<template>
  <div class="w-full">
    <div
      v-if="highlightContactInfo"
      class="flex items-center gap-2 px-3 py-2 text-xs text-n-slate-11 bg-n-slate-2 border-b border-n-slate-4"
    >
      <span
        class="inline-block w-3 h-3 rounded-sm bg-green-500/30 border border-green-500/50"
      ></span>
      <span>浅绿色背景 = 已留联系方式的客户（电话/邮箱/微信/QQ等）</span>
    </div>
    <table class="w-full text-sm border-collapse">
      <thead>
        <tr class="bg-n-slate-2 border-b border-n-slate-4 text-n-slate-11">
          <th class="px-3 py-2 text-left font-medium w-10">序号</th>
          <th class="px-3 py-2 text-left font-medium">访客名称</th>
          <th class="px-3 py-2 text-left font-medium">访客id</th>
          <th class="px-3 py-2 text-left font-medium">联系方式</th>
          <th class="px-3 py-2 text-left font-medium">邮箱</th>
          <th class="px-3 py-2 text-left font-medium">来源</th>
          <th class="px-3 py-2 text-left font-medium">归属者</th>
          <th class="px-3 py-2 text-left font-medium">名片状态</th>
          <th class="px-3 py-2 text-left font-medium">保存时间</th>
          <th class="px-3 py-2 text-left font-medium">来源URL</th>
          <th class="px-3 py-2 text-left font-medium">IP</th>
          <th class="px-3 py-2 text-left font-medium">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(contact, index) in contacts"
          :key="contact.id"
          class="border-b border-n-slate-3 cursor-pointer transition-colors"
          :class="rowBgClass(contact)"
          @dblclick="onClickViewDetails(contact.id)"
        >
          <td class="px-3 py-2 text-n-slate-11">{{ rowNumberOf(index) }}</td>
          <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
            {{ visitorName(contact) }}
          </td>
          <td
            class="px-3 py-2 text-n-brand font-medium cursor-pointer hover:underline"
            @click.stop="onClickViewDetails(contact.id)"
            title="单击打开"
          >
            {{ contact.name || '-' }}
            <span
              v-if="duplicateMap[String(contact.id)]"
              class="ml-1 inline-block px-1.5 text-xs font-bold text-white rounded leading-tight transition-colors"
              :class="duplicateMap[String(contact.id)].is_first ? 'bg-[#16a34a]' : 'bg-[#d97706] hover:bg-[#b45309] cursor-pointer'"
              :title="duplicateMap[String(contact.id)].is_first ? '联系方式重复(首个)' : `联系方式重复(同: ${duplicateMap[String(contact.id)].first_name}) 点击查看`"
              @click.stop="!duplicateMap[String(contact.id)].is_first && onClickViewDetails(duplicateMap[String(contact.id)].first_id)"
            >重</span>
          </td>
          <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
            {{ contactMethodDisplay(contact) }}
          </td>
          <td class="px-3 py-2 text-n-slate-11">{{ contact.email || '-' }}</td>
          <td class="px-3 py-2 text-n-slate-11">
            {{ sourceLabel(contact.customAttributes?.source) }}
          </td>
          <td class="px-3 py-2 text-n-slate-11">
            {{ resolveOwner(contact) }}
          </td>
          <td class="px-3 py-2 text-n-slate-11">
            <div class="flex items-center gap-1.5">
              <span
                class="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                :class="
                  cardStatusColorClass(contact.customAttributes?.cardStatus)
                "
              ></span>
              <span>{{ contact.customAttributes?.cardStatus || '' }}</span>
            </div>
          </td>
          <td class="px-3 py-2 text-n-slate-11">
            {{ formatSavedTime(contact.customAttributes?.contactSavedAt) || formatEntryTime(contact.createdAt) }}
          </td>
          <td class="px-3 py-2 text-n-slate-11 max-w-[160px]">
            <a
              v-if="sourceUrl(contact)"
              :href="sourceUrl(contact)"
              target="_blank"
              rel="noopener nofollow noreferrer"
              :title="sourceUrl(contact)"
              class="text-n-brand hover:underline truncate inline-block max-w-full align-bottom"
            >
              {{ sourceUrl(contact) }}
            </a>
            <span v-else>-</span>
          </td>
          <td class="px-3 py-2 text-n-slate-11">
            {{ contact.additionalAttributes?.createdAtIp || '-' }}
          </td>
          <td class="px-3 py-2">
            <button
              class="text-xs text-n-brand hover:underline"
              @click.stop="onClickViewDetails(contact.id)"
            >
              查看
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
